import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Loader } from 'semantic-ui-react';

import Api from '@plone/volto/helpers/Api/Api';
import config from '@plone/volto/registry';

import { getHomepageContentUrl } from '../../helpers/api';
import {
  getActiveLanguage,
  isLanguageHomePath,
} from '../../helpers/homepageUrl';
import HomePageBlocksView from './HomePageBlocksView';

const pageForLanguage = (payload, language) =>
  payload?.language === language && payload.content ? payload.content : null;

const LanguageRootHomeView = (props) => {
  const { content } = props;
  const { pathname } = useLocation();
  const locale = useSelector((state) => state.intl?.locale);
  const storedHomepage = useSelector(
    (state) => state.reduxAsyncConnect?.homepage,
  );
  const language = getActiveLanguage(pathname, locale);
  const supportedLanguages = config.settings.supportedLanguages || [];

  const shouldLoadHomepage =
    content?.['@type'] === 'LRF' &&
    supportedLanguages.includes(language) &&
    isLanguageHomePath(pathname);

  const syncedContent = pageForLanguage(storedHomepage, language);

  const [fallback, setFallback] = useState(null);

  useEffect(() => {
    if (!shouldLoadHomepage || syncedContent) {
      return undefined;
    }
    if (fallback?.language === language) {
      return undefined;
    }

    let active = true;
    const request = new Api().get(getHomepageContentUrl(language));

    request
      .then((data) => {
        if (active) {
          setFallback({ language, content: data });
        }
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setFallback({ language, error: true });
      });

    return () => {
      active = false;
      request.request?.abort();
    };
  }, [fallback, language, shouldLoadHomepage, syncedContent]);

  if (!shouldLoadHomepage) {
    return null;
  }

  const homepageContent = syncedContent || pageForLanguage(fallback, language);

  if (!homepageContent) {
    if (fallback?.language === language && fallback.error) {
      return null;
    }
    return (
      <div className="csj-page-loader">
        <Loader active inline="centered" />
      </div>
    );
  }

  return <HomePageBlocksView {...props} content={homepageContent} />;
};

LanguageRootHomeView.propTypes = {
  content: PropTypes.objectOf(PropTypes.any),
};

export default LanguageRootHomeView;
